/*-----------------------------------------------------------------------------
  PDP standard lifestyle carousel

  Keeps the optional brand-story carousel accessible without duplicating the
  theme's media-carousel implementation. The controller intentionally uses
  the public media-carousel API only.
-----------------------------------------------------------------------------*/

(() => {
    const ROOT_SELECTOR = "[data-pdp-lifestyle-carousel]";
    const TOGGLE_SELECTOR = "[data-pdp-lifestyle-toggle]";
    const CAPTION_SELECTOR = "[data-pdp-lifestyle-caption]";
    const CAROUSEL_SELECTOR = "media-carousel";
    const INTERACTION_SELECTOR = [ ".js-carousel-prev", ".js-carousel-next", ".js-carousel-pagination", ".swiper-pagination-bullet", "[data-pdp-lifestyle-navigation]" ].join(",");
    const PLAYING_LABEL = "Pause slideshow";
    const PAUSED_LABEL = "Play slideshow";
    const REDUCED_MOTION_LABEL = "Slideshow autoplay is disabled because reduced motion is enabled";
    const controllers = new WeakMap();
    const globalKey = "PDPStandardLifestyleCarousel";

    class PdpLifestyleCarouselController {
        constructor(root) {
            this.root = root;
            this.carousel = root.querySelector(CAROUSEL_SELECTOR);
            this.toggle = root.querySelector(TOGGLE_SELECTOR);
            this.destroyed = false;
            this.userPaused = false;
            this.isPlaying = false;
            this.pointerStart = null;
            this.motionQuery = typeof window.matchMedia === "function" ? window.matchMedia("(prefers-reduced-motion: reduce)") : null;
            this.hasAutoplay = Boolean(this.carousel?.hasAttribute("data-autoplay"));

            if (!this.carousel) return;

            this.handleCarouselUpdate = this.handleCarouselUpdate.bind(this);
            this.handleToggle = this.handleToggle.bind(this);
            this.handleClick = this.handleClick.bind(this);
            this.handleKeydown = this.handleKeydown.bind(this);
            this.handlePointerDown = this.handlePointerDown.bind(this);
            this.handlePointerUp = this.handlePointerUp.bind(this);
            this.handlePointerCancel = this.handlePointerCancel.bind(this);
            this.handleTouchStart = this.handleTouchStart.bind(this);
            this.handleTouchEnd = this.handleTouchEnd.bind(this);
            this.handleMotionPreferenceChange = this.handleMotionPreferenceChange.bind(this);

            this.carousel.addEventListener("on:media-carousel:init", this.handleCarouselUpdate);
            this.carousel.addEventListener("on:media-carousel:slide-change", this.handleCarouselUpdate);
            this.root.addEventListener("click", this.handleClick);
            this.root.addEventListener("keydown", this.handleKeydown);

            if (this.toggle) this.toggle.addEventListener("click", this.handleToggle);

            if ("PointerEvent" in window) {
                this.carousel.addEventListener("pointerdown", this.handlePointerDown);
                this.carousel.addEventListener("pointerup", this.handlePointerUp);
                this.carousel.addEventListener("pointercancel", this.handlePointerCancel);
            } else {
                this.carousel.addEventListener("touchstart", this.handleTouchStart, { passive: true });
                this.carousel.addEventListener("touchend", this.handleTouchEnd, { passive: true });
                this.carousel.addEventListener("touchcancel", this.handlePointerCancel, { passive: true });
            }

            if (this.motionQuery) {
                if (typeof this.motionQuery.addEventListener === "function") {
                    this.motionQuery.addEventListener("change", this.handleMotionPreferenceChange);
                } else if (typeof this.motionQuery.addListener === "function") {
                    this.motionQuery.addListener(this.handleMotionPreferenceChange);
                }
            }

            this.updateCaptions(this.currentIndex());
            this.syncPlayback();

            if (window.customElements?.whenDefined) {
                window.customElements.whenDefined("media-carousel").then(() => {
                    if (!this.destroyed) this.syncPlayback();
                }).catch(() => {});
            }
        }

        currentIndex() {
            const index = Number(this.carousel?.index);
            return Number.isFinite(index) ? index : 0;
        }

        prefersReducedMotion() {
            return Boolean(this.motionQuery?.matches);
        }

        safeCarouselCall(method) {
            if (typeof this.carousel?.[method] !== "function") return;

            try {
                this.carousel[method]();
            } catch (error) {
                // The media-carousel may still be waiting for its intersection
                // observer. Its init event will call this method again.
            }
        }

        syncPlayback(force = false) {
            const canPlay = this.hasAutoplay && !this.userPaused && !this.prefersReducedMotion();
            const wasPlaying = this.isPlaying;

            this.isPlaying = canPlay;

            if (canPlay && (force || !wasPlaying)) {
                this.safeCarouselCall("start");
            } else {
                if (!canPlay) this.safeCarouselCall("stop");
            }

            this.updateToggle();
        }

        pauseForUserInteraction() {
            if (!this.hasAutoplay || this.userPaused) return;

            this.userPaused = true;
            this.syncPlayback();
        }

        handleCarouselUpdate(event) {
            const index = Number(event.detail?.activeIndex);
            this.updateCaptions(Number.isFinite(index) ? index : this.currentIndex());
            this.syncPlayback(event.type === "on:media-carousel:init");
        }

        handleToggle(event) {
            event.preventDefault();

            if (!this.hasAutoplay || this.prefersReducedMotion()) return;

            this.userPaused = this.isPlaying;
            this.syncPlayback();
        }

        handleClick(event) {
            const control = event.target.closest(INTERACTION_SELECTOR);
            if (!control || !this.root.contains(control)) return;

            this.pauseForUserInteraction();
        }

        handleKeydown(event) {
            const keysThatChangeSlides = [ "ArrowLeft", "ArrowRight", "Home", "End", "PageUp", "PageDown", "Enter", " " ];
            const target = event.target;

            if (!keysThatChangeSlides.includes(event.key) || target === this.toggle || !this.root.contains(target)) return;

            if (target.closest(CAROUSEL_SELECTOR) || target.closest(INTERACTION_SELECTOR)) {
                this.pauseForUserInteraction();
            }
        }

        handlePointerDown(event) {
            if (event.pointerType === "mouse" && event.button !== 0) return;

            this.pointerStart = {
                id: event.pointerId,
                x: event.clientX,
                y: event.clientY
            };
        }

        handlePointerUp(event) {
            if (!this.pointerStart || event.pointerId !== this.pointerStart.id) return;

            const horizontalDistance = Math.abs(event.clientX - this.pointerStart.x);
            const verticalDistance = Math.abs(event.clientY - this.pointerStart.y);
            this.pointerStart = null;

            if (horizontalDistance >= 24 && horizontalDistance > verticalDistance) {
                this.pauseForUserInteraction();
            }
        }

        handlePointerCancel() {
            this.pointerStart = null;
        }

        handleTouchStart(event) {
            const touch = event.touches?.[0];
            if (!touch) return;

            this.pointerStart = {
                x: touch.clientX,
                y: touch.clientY
            };
        }

        handleTouchEnd(event) {
            const touch = event.changedTouches?.[0];
            if (!touch || !this.pointerStart) return;

            const horizontalDistance = Math.abs(touch.clientX - this.pointerStart.x);
            const verticalDistance = Math.abs(touch.clientY - this.pointerStart.y);
            this.pointerStart = null;

            if (horizontalDistance >= 24 && horizontalDistance > verticalDistance) {
                this.pauseForUserInteraction();
            }
        }

        handleMotionPreferenceChange() {
            this.syncPlayback();
        }

        updateCaptions(activeIndex) {
            const captions = Array.from(this.root.querySelectorAll(CAPTION_SELECTOR)).filter(caption => !this.carousel.contains(caption));
            const hasExplicitIndexes = captions.some(caption => this.captionIndex(caption) !== null);

            captions.forEach((caption, fallbackIndex) => {
                const explicitIndex = this.captionIndex(caption);
                const index = explicitIndex === null ? fallbackIndex : explicitIndex;
                const isActive = captions.length === 1 && !hasExplicitIndexes || index === activeIndex;

                caption.hidden = !isActive;
                caption.setAttribute("aria-hidden", String(!isActive));
                caption.classList.toggle("is-active", isActive);
            });
        }

        captionIndex(caption) {
            const candidate = caption.dataset.slideIndex ?? caption.getAttribute("data-pdp-lifestyle-caption");
            if (candidate === null || candidate === "") return null;

            const index = Number(candidate);
            return Number.isFinite(index) ? index : null;
        }

        updateToggle() {
            if (!this.toggle) return;

            const reducedMotion = this.prefersReducedMotion();
            const label = reducedMotion ? REDUCED_MOTION_LABEL : this.isPlaying ? this.playingLabel() : this.pausedLabel();

            this.toggle.disabled = !this.hasAutoplay || reducedMotion;
            this.toggle.setAttribute("aria-disabled", String(!this.hasAutoplay || reducedMotion));
            this.toggle.setAttribute("aria-pressed", String(!this.isPlaying));
            this.toggle.setAttribute("aria-label", label);
            this.toggle.setAttribute("title", label);
            this.toggle.dataset.state = this.isPlaying ? "playing" : "paused";

            const textElement = this.toggle.querySelector("[data-pdp-lifestyle-toggle-label], [data-pdp-lifestyle-toggle-text]");
            if (textElement) {
                textElement.textContent = label;
            } else if (this.toggle.childElementCount === 0) {
                this.toggle.textContent = label;
            }
        }

        playingLabel() {
            return this.toggle?.dataset.playingLabel || PLAYING_LABEL;
        }

        pausedLabel() {
            return this.toggle?.dataset.pausedLabel || PAUSED_LABEL;
        }

        destroy() {
            if (this.destroyed) return;

            this.destroyed = true;
            this.carousel.removeEventListener("on:media-carousel:init", this.handleCarouselUpdate);
            this.carousel.removeEventListener("on:media-carousel:slide-change", this.handleCarouselUpdate);
            this.root.removeEventListener("click", this.handleClick);
            this.root.removeEventListener("keydown", this.handleKeydown);
            this.toggle?.removeEventListener("click", this.handleToggle);

            if ("PointerEvent" in window) {
                this.carousel.removeEventListener("pointerdown", this.handlePointerDown);
                this.carousel.removeEventListener("pointerup", this.handlePointerUp);
                this.carousel.removeEventListener("pointercancel", this.handlePointerCancel);
            } else {
                this.carousel.removeEventListener("touchstart", this.handleTouchStart);
                this.carousel.removeEventListener("touchend", this.handleTouchEnd);
                this.carousel.removeEventListener("touchcancel", this.handlePointerCancel);
            }

            if (this.motionQuery) {
                if (typeof this.motionQuery.removeEventListener === "function") {
                    this.motionQuery.removeEventListener("change", this.handleMotionPreferenceChange);
                } else if (typeof this.motionQuery.removeListener === "function") {
                    this.motionQuery.removeListener(this.handleMotionPreferenceChange);
                }
            }

            controllers.delete(this.root);
        }
    }

    const matchingRoots = root => {
        if (!root?.querySelectorAll) return [];

        return [ ...(root.matches?.(ROOT_SELECTOR) ? [ root ] : []), ...root.querySelectorAll(ROOT_SELECTOR) ];
    };

    const initialize = (root = document) => {
        matchingRoots(root).forEach(carouselRoot => {
            if (controllers.has(carouselRoot)) return;

            const controller = new PdpLifestyleCarouselController(carouselRoot);
            if (controller.carousel) controllers.set(carouselRoot, controller);
        });
    };

    const destroy = (root = document) => {
        matchingRoots(root).forEach(carouselRoot => controllers.get(carouselRoot)?.destroy());
    };

    if (window[globalKey]) {
        window[globalKey].initialize(document);
        return;
    }

    window[globalKey] = {
        initialize,
        destroy
    };

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () => initialize(document), {
            once: true
        });
    } else {
        initialize(document);
    }

    document.addEventListener("shopify:section:load", event => initialize(event.target));
    document.addEventListener("shopify:section:unload", event => destroy(event.target));
})();
